import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShifts, useDeleteShift, useDuplicateShift, useActivateShift, useDeactivateShift } from '../hooks';
import { ShiftCard, ShiftListItem } from '../components';
import { Button, DeleteConfirmationModal, FiltersButton, FilterBottomSheet, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, PlainHeadCell, SelectAllHeadCell, SortableHeadCell, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import type { ShiftQuery, ShiftResponse, ActiveStatus } from '@troia-v3/types';

type SortKey = 'name' | 'schedule' | 'status';

/** Extrai o startTime do turno para ordenação (suporta shape flat e legacy nested). */
const getStartTime = (shift: ShiftResponse): string => {
  const sched = (shift.schedule as { fixedSchedule?: ShiftResponse['schedule'] })?.fixedSchedule ?? shift.schedule;
  return sched?.startTime ?? '';
};

export const ShiftsPage: React.FC = () => {
  const navigate = useNavigate();

  // "+" central do bottom-nav mobile → novo item (mockup .m-fab).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); navigate('/settings/shifts/new'); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [navigate]);
  // Persiste a preferência de view (as demais listas de settings persistem)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>(() => {
    try { return localStorage.getItem('shifts-viewmode') === 'table' ? 'table' : 'grid'; } catch { return 'grid'; }
  });
  useEffect(() => {
    try { localStorage.setItem('shifts-viewmode', viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<ShiftQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { data, isLoading, error } = useShifts(query);
  const deleteShift = useDeleteShift();
  const duplicateShift = useDuplicateShift();
  const activateShift = useActivateShift();
  const deactivateShift = useDeactivateShift();

  // Dropdown cross-module (rota /dropdown sem permissão) para o filtro Equipe.
  const { data: teamsData } = useTeamsDropdown({ enabled: filtersOpen || !!query.teamId });
  const teams = useMemo(() => teamsData ?? [], [teamsData]);

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort para turnos).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'schedule') cmp = getStartTime(a).localeCompare(getStartTime(b));
      else cmp = (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((s) => selectedIds.has(s.id));
  const someOnPageSelected = items.some((s) => selectedIds.has(s.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: ActiveStatus) => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };

  const setTeamFilter = (teamId?: string) => {
    setQuery((prev) => ({ ...prev, teamId, page: 1 }));
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, teamId: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((s) => next.delete(s.id));
      else items.forEach((s) => next.add(s.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleEdit = (id: string) => navigate(`/settings/shifts/${id}/edit`);
  const handleManageMembers = (id: string) => navigate(`/settings/shifts/${id}/edit`);

  const handleDuplicate = (id: string) => duplicateShift.mutate(id);

  const handleDelete = (id: string) => setDeleteId(id);

  const confirmDelete = () => {
    if (deleteId) deleteShift.mutate(deleteId);
    setDeleteId(null);
  };

  const handleToggleStatus = (id: string, currentStatus: string) => {
    if (currentStatus === 'active') deactivateShift.mutate(id);
    else activateShift.mutate(id);
  };

  const bulkDeactivate = () => {
    const targets = items.filter((s) => selectedIds.has(s.id) && s.status === 'active');
    targets.forEach((s) => deactivateShift.mutate(s.id));
    clearSelection();
  };

  const bulkDelete = () => setBulkDeleteOpen(true);

  const confirmBulkDelete = () => {
    Array.from(selectedIds).forEach((id) => deleteShift.mutate(id));
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const statusLabel = query.status === 'active' ? 'Ativos' : query.status === 'inactive' ? 'Inativos' : null;
  const teamLabel = query.teamId ? (teams.find((t) => t.id === query.teamId)?.name ?? null) : null;
  const activeFilterCount = [statusLabel, teamLabel].filter(Boolean).length;

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {([[undefined, 'Todos'], ['active', 'Ativos'], ['inactive', 'Inativos']] as const).map(([val, label]) => {
        const active = query.status === val;
        return (
          <button
            key={label}
            onClick={() => setStatusFilter(val)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}

      {/* Equipe */}
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Equipe</p>
      <button
        onClick={() => setTeamFilter(undefined)}
        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
        style={{ color: '#374151', backgroundColor: !query.teamId ? '#f3e8ff' : 'transparent' }}
        onMouseEnter={(e) => { if (query.teamId) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
        onMouseLeave={(e) => { if (query.teamId) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        Todas
        {!query.teamId && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
      </button>
      {teams.map((team) => {
        const active = query.teamId === team.id;
        return (
          <button
            key={team.id}
            onClick={() => setTeamFilter(team.id)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span className="truncate">{team.name}</span>
            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate('/settings')}
              className="text-sm font-medium truncate hidden sm:inline"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Configurações
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Turnos</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          /* Centro: busca (F17-22: ToolbarSearch shared, sempre centralizada) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar turno…"
            data-testid="shifts-search"
          />
        }
        right={<>
          {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros (F05: FiltersButton shared) */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="shifts-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="shifts-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: ViewToggle shared — Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="shifts-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Novo turno (único primário — F01-03: Button shared com colapso) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => navigate('/settings/shifts/new')}
            data-testid="new-shift"
          >
            Novo turno
          </Button>
        </>}
        mobile={
          /* `.m-sub` do mockup: busca sunken + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar turno…"
              data-testid="shifts-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="shifts-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {teamLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Equipe: {teamLabel}
              <button onClick={() => setTeamFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de equipe">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }}>Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}>
            <Icon name="power" className="w-3.5 h-3.5" /> Desativar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}>
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }}>Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar turnos: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhum turno encontrado"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie seu primeiro turno para organizar os horários da equipe'}
              actionLabel="Novo turno"
              onAction={() => navigate('/settings/shifts/new')}
            />
          )}

          {/* Grid (Cards) — F34: cards SEM checkbox, seleção em massa só na tabela */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sortedItems.map((shift: ShiftResponse) => (
                <ShiftCard
                  key={shift.id}
                  shift={shift}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus} onManageMembers={handleManageMembers} onDuplicate={handleDuplicate}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((shift: ShiftResponse) => (
                  <ShiftCard
                    key={shift.id}
                    shift={shift}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus} onManageMembers={handleManageMembers} onDuplicate={handleDuplicate}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        ariaLabel="Selecionar todos"
                        data-testid="shifts-select-all"
                      />
                      <SortableHeadCell<SortKey> label="Nome" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Descrição" />
                      <PlainHeadCell label="Tipo" />
                      <SortableHeadCell<SortKey> label="Horário" sortKey="schedule" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Membros" />
                      <SortableHeadCell<SortKey> label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((shift: ShiftResponse) => (
                      <ShiftListItem
                        key={shift.id}
                        shift={shift}
                        selected={selectedIds.has(shift.id)}
                        onToggleSelect={toggleSelect}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus} onManageMembers={handleManageMembers} onDuplicate={handleDuplicate}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              totalItems={data.total}
              pageSize={data.limit}
              itemLabel="turnos"
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="shifts-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      {/* Confirmação de exclusão (single) */}
      <DeleteConfirmationModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        message="Tem certeza que deseja deletar este turno?"
        isLoading={deleteShift.isPending}
      />

      {/* Confirmação de exclusão (bulk) */}
      <DeleteConfirmationModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        message={`Excluir ${selectedCount} turno(s) selecionado(s)?`}
        isLoading={deleteShift.isPending}
      />
    </div>
  );
};

ShiftsPage.displayName = 'ShiftsPage';
